import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { VoiceRecorder } from '@/components/voice/VoiceRecorder';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import {
  campaignsService,
  AiCampaignAssistRequest,
  AiCampaignAssistResponse,
  AiProviderName,
} from '@/services/campaigns.service';
import { settingsService, AiModelsResponse } from '@/services/settings.service';
import { voiceService } from '@/services/voice.service';
import toast from 'react-hot-toast';

interface Props {
  open: boolean;
  onClose: () => void;
  templateUid: string;
  templateName?: string;
  onApply: (response: AiCampaignAssistResponse) => void;
}

// Persist last model choice across modal opens (matches template modal pattern).
let persistedProvider: AiProviderName | null = null;
let persistedModel: string | null = null;

export const CampaignAiAssistModal: React.FC<Props> = ({ open, onClose, templateUid, templateName, onApply }) => {
  const [brief, setBrief] = useState('');
  const [tone, setTone] = useState<AiCampaignAssistRequest['tone']>('promotional');
  const [busy, setBusy] = useState(false);
  const [showDictation, setShowDictation] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const [modelsData, setModelsData] = useState<AiModelsResponse | null>(null);
  const [provider, setProvider] = useState<AiProviderName>(persistedProvider ?? 'openai');
  const [model, setModel] = useState<string>(persistedModel ?? 'gpt-4o-mini');
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    settingsService.listAiModels().then((d) => {
      setModelsData(d);
      if (!persistedProvider) {
        setProvider(d.default.provider as AiProviderName);
        setModel(d.default.model);
      }
    }).catch(() => { /* fallback list still works */ });
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await campaignsService.aiAssist({ brief, templateUid, tone, provider, model });
      toast.success(`Generated · ${r.creditsCharged ?? 0} credits charged`);
      onApply(r);
      setBrief('');
    } catch { /* axios interceptor toasts */ } finally { setBusy(false); }
  };

  const availableProviders = modelsData?.providers.filter(p => p.available) ?? [];

  return (
    <Modal open={open} onClose={onClose} title="AI Campaign Assist" widthClass="max-w-xl">
      <div className="space-y-3">
        <p className="text-xs text-text-muted">
          {templateName
            ? <>Describe who this campaign is for and what action you want — AI will fill the campaign name, description, and the <strong>{templateName}</strong> variables. Always review before launching.</>
            : <>Describe who this campaign is for and what action you want — AI will fill the campaign name, description, and template variables. Always review before launching.</>}
        </p>

        <FormField label="Brief" required>
          <div className="mb-1.5 flex justify-end">
            <EnhancePromptButton value={brief} onChange={setBrief} context="WhatsApp campaign brief" disabled={busy} provider={provider} model={model} />
          </div>
          <Textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={4}
            placeholder="Re-engagement push to dormant Karachi customers who haven't ordered in 60 days; flat 15% off any order over Rs 2000; expires Sunday midnight"
          />
          {showDictation ? (
            <div className="mt-2">
              <VoiceRecorder
                sending={transcribing}
                onDiscard={() => setShowDictation(false)}
                onSend={async (blob) => {
                  setTranscribing(true);
                  try {
                    const audioFile = new File([blob], 'brief.webm', { type: blob.type || 'audio/webm' });
                    const result = await voiceService.transcribe(audioFile);
                    setBrief((prev) => prev ? `${prev} ${result.text}` : result.text);
                    setShowDictation(false);
                    toast.success('Transcribed and appended to brief');
                  } catch {
                    toast.error('Transcription failed');
                  } finally {
                    setTranscribing(false);
                  }
                }}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDictation(true)}
              className="mt-1.5 flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-surface-alt">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
              </span>
              Dictate brief
            </button>
          )}
        </FormField>

        <FormField label="Tone">
          <Select value={tone} onChange={(e) => setTone(e.target.value as AiCampaignAssistRequest['tone'])}>
            <option value="promotional">Promotional</option>
            <option value="friendly">Friendly</option>
            <option value="formal">Formal</option>
            <option value="transactional">Transactional</option>
          </Select>
        </FormField>

        <FormField label="Model">
          {/* Models come from the central registry — never hardcoded here. */}
          <Select
            value={`${provider}::${model}`}
            disabled={availableProviders.length === 0}
            onChange={(e) => {
              const [p, m] = e.target.value.split('::') as [AiProviderName, string];
              setProvider(p); setModel(m);
              persistedProvider = p; persistedModel = m;
            }}
          >
            {availableProviders.length === 0 && (
              <option value="">Loading models…</option>
            )}
            {availableProviders.map(entry => (
              <optgroup key={entry.id} label={entry.label}>
                {entry.models.map(m => (
                  <option key={m.id} value={`${entry.id}::${m.id}`}>{m.label}</option>
                ))}
              </optgroup>
            ))}
          </Select>
        </FormField>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button disabled={busy || !brief || !templateUid} onClick={submit}>
          <span className="inline-flex items-center gap-2">
            <Sparkles size={14} /> {busy ? 'Generating…' : 'Generate'}
          </span>
        </Button>
      </div>
    </Modal>
  );
};
